import { useState } from "react";
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { SWR_DEFAULT } from '../constants/financial';
import { F, FF } from '../utils/format';
import { Card } from '../components/Card';
import { Stat } from '../components/Stat';
import { Heading } from '../components/Heading';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { DeltaBadge } from '../components/DeltaBadge';
import { FIRing } from '../components/FIRing';
import { TrendingUp, PiggyBank, Zap, ArrowUpRight, ArrowDownRight, Sparkles } from '../components/icons';
import { useApp } from '../hooks/useApp';
import StreakWidget from '../features/streaks/StreakWidget';
import QuestPanel from '../features/quests/QuestPanel';
import BadgeCase from '../features/rewards/BadgeCase';
import InsightBanner from '../features/motivation/InsightBanner';
import { overviewInsights } from '../features/motivation/motivationEngine';
import PeerContext from '../features/social/PeerContext';
import ShareModal from '../features/sharing/ShareModal';

export default function Overview() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const [shareOpen, setShareOpen] = useState(false);
  const insights = overviewInsights(s, proj, pF, fiAge, fiReached, coastAge);

  const alloc = [
    { n: "Investments", v: pF.liq, cl: c.blue },
    { n: "Home Equity", v: pF.eq, cl: c.green },
    { n: "Pension PV", v: pF.pPV, cl: c.orange },
  ];
  const w = proj.find((d) => d.a >= s.startAge) || p0;
  const sr = w.comp > 0 ? Math.round((w.sv / w.comp) * 100) : 0;
  const burn = budgetMonthly;
  const fiTarget = (pF.liv || s.liv) / SWR_DEFAULT;
  const fiProg = fiTarget > 0 ? Math.min(100, (s.nw0 / fiTarget) * 100) : 0;

  const y1 = proj.find((d) => d.a === s.age + 1);
  const yoyNW = y1 ? y1.nw - (proj.find(d => d.a === s.age)?.nw || 0) : 0;

  const actualNow = s.manNW?.[s.age];
  const projectedNow = proj.find(d => d.a === s.age)?.nw;
  const planDelta = actualNow && projectedNow ? actualNow - projectedNow : null;

  const nwTrack = proj.map((d) => ({
    a: d.a, projected: d.nw,
    actual: s.manNW?.[d.a] || null,
  }));

  return (
    <Stack>
      {/* Hero */}
      <Card c={c} sx={{ padding: "32px 28px", background: s.dark
        ? "linear-gradient(135deg, #1C1C1E 0%, #0D1117 50%, #0A0A0A 100%)"
        : "linear-gradient(135deg, #FFFFFF 0%, #F8F9FF 50%, #F2F2F7 100%)",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              Projected Net Worth at {pF.a}
            </div>
            <div style={{ fontSize: 52, fontWeight: 800, letterSpacing: "-0.05em", lineHeight: 1, color: c.fg }}>
              {F(pF.nw)}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 14px", borderRadius: 20,
                background: s.dark ? "rgba(48,209,88,0.12)" : "rgba(48,209,88,0.08)",
                fontSize: 13, fontWeight: 600, color: c.green,
              }}>
                <ArrowUpRight size={14} />{Math.round(pF.nw / Math.max(1, s.nw0))}× from today
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 14px", borderRadius: 20,
                background: s.dark ? "rgba(10,132,255,0.12)" : "rgba(10,132,255,0.06)",
                fontSize: 13, fontWeight: 600, color: c.blue,
              }}>
                <Sparkles size={13} />{fiReached ? `FI by ${fiAge}` : `FI target: ${F(fiTarget)}`}
              </div>
              {planDelta !== null && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "5px 14px", borderRadius: 20,
                  background: planDelta >= 0
                    ? (s.dark ? "rgba(48,209,88,0.12)" : "rgba(48,209,88,0.06)")
                    : (s.dark ? "rgba(255,55,95,0.12)" : "rgba(255,55,95,0.06)"),
                  fontSize: 13, fontWeight: 600,
                  color: planDelta >= 0 ? c.green : c.red,
                }}>
                  {planDelta >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {planDelta >= 0 ? `${F(planDelta)} ahead` : `${F(Math.abs(planDelta))} behind`}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
            <FIRing progress={fiProg} size={110} c={c} />
            <button
              onClick={() => setShareOpen(true)}
              style={{
                padding: "10px 18px", borderRadius: 12,
                border: `1px solid ${c.border}`, background: "transparent",
                color: c.blue, fontSize: 12, fontWeight: 600,
                cursor: "pointer", whiteSpace: "nowrap",
                transition: "all 0.2s ease",
              }}
            >
              📤 Share
            </button>
          </div>
        </div>
      </Card>

      {/* Streak */}
      <StreakWidget />

      {/* Quests */}
      <QuestPanel />

      {/* Badges */}
      <BadgeCase />

      {/* Insights */}
      <InsightBanner insights={insights} />

      {/* Metric strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {[
          { l: "Current NW", v: FF(s.nw0), d: yoyNW },
          { l: "FI Age", v: fiReached ? fiAge : `>${s.maxAge || 40}`, cl: fiReached ? c.blue : c.orange },
          { l: "Coast FI", v: coastAge ? `Age ${coastAge}` : "—", cl: c.teal },
          { l: "4% SWR", v: `${F(pF.fi)}/yr`, cl: c.green },
          { l: "Savings Rate", v: `${sr}%`, cl: c.purple },
          { l: "Monthly Burn", v: FF(burn), cl: c.red },
        ].map((x, i) => (
          <Card key={x.l} c={c} delay={i * 0.04} sx={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>{x.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", marginTop: 5, color: x.cl || c.fg }}>{x.v}</div>
            {x.d ? <DeltaBadge val={x.d} c={c} suffix="/yr" /> : null}
          </Card>
        ))}
      </div>

      {/* Asset cards */}
      <Grid min={200}>
        <Card glow c={c} delay={0.1}>
          <Stat l="Liquid Investments" v={F(pF.liq)} sub={`FHSA ${F(pF.fhsa)} · TFSA ${F(pF.tfsa)}`} c={c} />
        </Card>
        <Card c={c} delay={0.15}>
          <Stat l="Home Equity" v={F(pF.eq)} sub={`${F(pF.hv)} property`} c={c} />
        </Card>
        <Card c={c} delay={0.2}>
          <Stat l="Pension PV" v={F(pF.pPV)} sub={`${F(pF.pA)}/yr · starts age ${s.penAge || 60}`} c={c} />
        </Card>
      </Grid>

      {/* NW Trajectory */}
      <Card c={c} delay={0.2}>
        <Heading ic={TrendingUp} c={c}>Net Worth Trajectory</Heading>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={nwTrack}>
            <defs>
              <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.blue} stopOpacity={0.25} />
                <stop offset="100%" stopColor={c.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
            <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => (v != null ? FF(v) : "—")} {...tt} />
            <Area type="monotone" dataKey="projected" stroke={c.blue} strokeWidth={2.5} fill="url(#gInv)" name="Projected" />
            <Line type="monotone" dataKey="actual" stroke={c.green} strokeWidth={2.5} dot={{ fill: c.green, r: 4, strokeWidth: 0 }} name="Actual" connectNulls={false} />
          </AreaChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 12, color: c.muted, marginTop: 8 }}>
          Track actuals in Settings → they overlay as green dots
        </div>
      </Card>

      {/* Allocation + Cash flow */}
      <Grid>
        <Card c={c} delay={0.25}>
          <Heading ic={PiggyBank} c={c}>Allocation at {pF.a}</Heading>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <ResponsiveContainer width={130} height={130}>
              <PieChart>
                <Pie data={alloc} cx="50%" cy="50%" outerRadius={58} innerRadius={42} dataKey="v" paddingAngle={3} strokeWidth={0}>
                  {alloc.map((e, i) => <Cell key={i} fill={e.cl} />)}
                </Pie>
                <Tooltip formatter={(v) => FF(v)} {...tt} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {alloc.map((a) => (
                <div key={a.n} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: a.cl, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, color: c.muted }}>{a.n}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: c.fg }}>{F(a.v)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card c={c} delay={0.3}>
          <Heading ic={Zap} c={c}>Monthly Cash Flow (Age {w.a})</Heading>
          {[
            { l: "After-Tax Income", v: Math.round(w.afterTax / 12), pos: true },
            { l: "Living Expenses", v: -Math.round(w.liv / 12) },
            { l: "Mortgage", v: w.a >= s.hAge ? -w.moP : 0 },
            { l: "Savings", v: -Math.round(w.sv / 12) },
          ].map((x) => (
            <div key={x.l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${c.border}` }}>
              <span style={{ fontSize: 13, color: c.muted }}>{x.l}</span>
              <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: x.pos ? c.green : c.fg }}>
                {FF(x.v)}/mo
              </span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 0" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.fg }}>Surplus</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: w.sur >= 0 ? c.green : c.red }}>
              {FF(Math.round(w.sur / 12))}/mo
            </span>
          </div>
          {w.sur < 0 && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, background: s.dark ? "rgba(255,55,95,0.08)" : "rgba(255,55,95,0.05)", fontSize: 12, color: c.red, fontWeight: 500 }}>
              ⚠ Deficit — reduce expenses, delay house, or lower savings target
            </div>
          )}
        </Card>
      </Grid>

      {/* Peer Comparison */}
      <PeerContext />

      {/* Share Modal */}
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </Stack>
  );
}
